import type { QualityCandidateCheckResp, QualityCandidatesResp } from "@/api/diagnostics";
import { VIDEO_FREEZE_CANDIDATE_KIND } from "@/api/diagnosticQualityCandidates";

/**
 * 영상 정지 후보를 칩과 행이 쓰는 구간 모델로 바꾼다.
 *
 * 후보는 제안일 뿐이다. 리뷰어가 구간을 보고 결함 태그를 직접 붙인다. 검토 완료는 후보를 "확인함"
 * 으로 기록할 뿐 결함으로 판정하지 않는다. 그래서 확인한 후보도 계속 그린다. 이 모듈은 구간의
 * 위치와 확인 기록만 다루고 문구를 만들지 않는다. 재생 바 행과 문구는 `segmentTimeline` 이 만든다.
 *
 * 초는 영상 첫 프레임부터 잰 값이다. 그래서 영상 시각으로 그대로 쓴다. 출처 띠와 달리 음수가 없다.
 */

// 행 이름이자 툴팁 문구의 머리다. 필터 축 이름(`영상 정지 후보`)과 같다.
export const FREEZE_CANDIDATE_NAME = "영상 정지 후보";
// 칩에 보이는 짧은 이름. 순번은 칩에 함께 적는다.
const FREEZE_CANDIDATE_ABBR = "정지";

// 칩 색 점. 재생 바 행의 띠(`dataViz.blue`)와 같은 색이다. 출처 띠가 쓰지 않는 색이라서 두 행이
// 섞이지 않는다. 결함 태그 칩의 호박색도 피한다. 후보와 태그가 연결된 것처럼 보이면 안 된다.
// 리터럴로 적는다. `styles.test.ts` 가 `var(--c6-*)` 이름을 c6 가 내보내는 이름과 대조한다.
export const QUALITY_CANDIDATE_DOT_COLOR = "var(--c6-color-data-viz-blue)";

/** 칩과 행이 쓰는 후보 구간 하나. 순번은 영상 정지 후보 안에서 센다(1부터). */
export type QualityCandidateSpan = {
  id: string;
  ordinal: number;
  ordinalCount: number;
  startSeconds: number;
  endSeconds: number;
  // 이 종류의 확인 기록이다. 확인은 종류 단위라서 같은 종류의 구간은 모두 같은 기록을 가진다.
  // 아직 확인하지 않았으면 null 이다.
  check: QualityCandidateCheckResp | null;
};

/**
 * 후보 응답에서 영상 정지 구간을 고른다. 상태(`state`)로 거르지 않는다. 확인한 후보는 `none` 이어도
 * 항목에 남고, 화면은 그 구간을 확인 표시와 함께 그린다. 모르는 종류는 버린다. 그 종류를 영상
 * 정지로 표시하지 않는다.
 */
export function freezeCandidateSpans(
  candidates: QualityCandidatesResp,
): readonly QualityCandidateSpan[] {
  const freezes = candidates.items.filter((item) => item.kind === VIDEO_FREEZE_CANDIDATE_KIND);
  const check =
    candidates.checks.find((entry) => entry.kind === VIDEO_FREEZE_CANDIDATE_KIND) ?? null;
  return freezes.map((item, index) => ({
    id: `${VIDEO_FREEZE_CANDIDATE_KIND}-${index}`,
    ordinal: index + 1,
    ordinalCount: freezes.length,
    startSeconds: item.start_seconds,
    endSeconds: item.end_seconds,
    check,
  }));
}

/** 칩에 보이는 문구. 예) `정지 1/2` */
export function freezeCandidateChipText(span: QualityCandidateSpan): string {
  return `${FREEZE_CANDIDATE_ABBR} ${span.ordinal}/${span.ordinalCount}`;
}

/** 이 시각(영상 초)을 포함하는 후보의 index. 어느 후보에도 들지 않으면 null 이다. */
export function freezeCandidateIndexAt(
  spans: readonly QualityCandidateSpan[],
  seconds: number,
): number | null {
  const index = spans.findIndex(
    (span) => seconds >= span.startSeconds && seconds < span.endSeconds,
  );
  return index < 0 ? null : index;
}
